/** How the financial settings screen presents a state the server decided.
 *
 * Nothing here decides whether financial answers render - that is the
 * readiness gate on the server. These helpers only choose between showing a
 * finished classification to read and a form to fill in, so a company whose
 * map is signed off is not left wondering what it still has to do.
 */
import type { ChartAccount, FinancialSettings } from './api';

/** The sign-off exists and still matches the map as it stands. */
export function isApprovedNow(data: FinancialSettings | null): boolean {
  return !!data?.checks.find((c) => c.id === 'map_approved')?.passed;
}

/** Signed off once, edited since: answers pause until someone approves again. */
export function approvalLapsed(data: FinancialSettings | null): boolean {
  return !!data?.approval && !isApprovedNow(data);
}

/** The only thing standing between this company and financial answers is
 *  approving the latest edit. */
export function pausedOnlyForApproval(data: FinancialSettings | null): boolean {
  if (!data || !approvalLapsed(data)) return false;
  const failing = data.checks.filter((c) => !c.passed).map((c) => c.id);
  return failing.length === 1 && failing[0] === 'map_approved';
}

/** Accounts that are only titles over other accounts: they have accounts
 *  beneath them, carry no balance, have no line of their own, and every
 *  account beneath them is classified. "Assets" or "Current Assets" in a
 *  chart where every leaf is already ruled on. They show as "Heading" rather
 *  than "Not classified", which read like missing data.
 *
 *  A title with an unclassified account somewhere beneath it is not a
 *  heading: ruling on it is how that account gets classified. */
export function headingCodes(accounts: ChartAccount[]): Set<string> {
  const children = new Map<string, ChartAccount[]>();
  accounts.forEach((a) => {
    if (!a.parent) return;
    const list = children.get(a.parent) ?? [];
    list.push(a);
    children.set(a.parent, list);
  });

  const settled = new Map<string, boolean>();
  const allBeneathSettled = (code: string, seen: Set<string>): boolean => {
    if (settled.has(code)) return settled.get(code)!;
    if (seen.has(code)) return false;
    seen.add(code);
    const ok = (children.get(code) ?? []).every(
      (c) =>
        (c.line !== null || Math.abs(c.balance) < 0.005) &&
        allBeneathSettled(c.code, seen)
    );
    settled.set(code, ok);
    return ok;
  };

  const out = new Set<string>();
  accounts.forEach((a) => {
    if (!children.has(a.code)) return;
    if (a.line !== null || Math.abs(a.balance) >= 0.005) return;
    if (allBeneathSettled(a.code, new Set())) out.add(a.code);
  });
  return out;
}
